// src/features/tenant/ExportTenantDialog.tsx
// Request a tenant data export — form-kit dialog for TenantDataExportPage
// (docs/ux/14-forms.md). Triggers an export job; history stays a list on
// the page behind the dialog.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow, SegmentedControl } from '@/components/form';

const EXPORT_FORMATS = ['JSON', 'CSV', 'SQL'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/** Map the short local format key to the wire enum value (DataExportFormat). */
export function toDataExportFormatEnum(format: ExportFormat): string {
  return `DATA_EXPORT_FORMAT_${format}`;
}

const EXPORT_SCOPES = [
  { value: 'members', labelKey: 'tenant.export.scopeMembers' },
  { value: 'settings', labelKey: 'tenant.export.scopeSettings' },
  { value: 'content', labelKey: 'tenant.export.scopeContent' },
  { value: 'audit_logs', labelKey: 'tenant.export.scopeAuditLogs' },
  { value: 'all', labelKey: 'tenant.export.scopeAll' },
] as const;

export type ExportScope = (typeof EXPORT_SCOPES)[number]['value'];

export interface ExportTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (format: ExportFormat, scopes: ExportScope[]) => Promise<void>;
}

export function ExportTenantDialog({ open, onClose, onSubmit }: ExportTenantDialogProps) {
  const { t } = useI18n();
  const [format, setFormat] = useState<ExportFormat>('JSON');
  const [selectedScopes, setSelectedScopes] = useState<Set<ExportScope>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFormat('JSON');
    setSelectedScopes(new Set());
    setError(null);
  }, [open]);

  const toggleScope = (scope: ExportScope) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (scope === 'all') {
        if (next.has('all')) next.delete('all');
        else {
          next.clear();
          next.add('all');
        }
      } else {
        next.delete('all');
        if (next.has(scope)) next.delete(scope);
        else next.add(scope);
      }
      return next;
    });
  };

  const dirty = selectedScopes.size > 0;

  const submit = async () => {
    if (selectedScopes.size === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(format, Array.from(selectedScopes));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('tenant.export.requestButton')}
      canSubmit={selectedScopes.size > 0}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('tenant.export.requestTitle')}
    >
      <div className="space-y-3 px-3.5 py-3">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <FormRow label={t('tenant.export.format')} labelWidth="sm">
          <SegmentedControl
            aria-label={t('tenant.export.format')}
            size="sm"
            value={format}
            onChange={(v) => setFormat(v as ExportFormat)}
            options={EXPORT_FORMATS.map((f) => ({ value: f, label: f }))}
          />
        </FormRow>
        <FormRow label={t('tenant.export.scopes')} labelWidth="sm">
          <div className="space-y-2">
            {EXPORT_SCOPES.map((scope) => (
              <label key={scope.value} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedScopes.has(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                  className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                />
                <span
                  className={`text-sm ${selectedScopes.has(scope.value) ? 'text-foreground' : 'text-muted-foreground'}`}
                >
                  {t(scope.labelKey)}
                </span>
              </label>
            ))}
          </div>
        </FormRow>
        {selectedScopes.size === 0 && (
          <p className="text-xs text-muted-foreground">{t('tenant.export.selectScope')}</p>
        )}
      </div>
    </FormDialog>
  );
}

export default ExportTenantDialog;
